import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { environmentVariables } from '../utils/environmentVariables'

const COUNTDOWN_DURATION = Number.parseInt(environmentVariables('COUNTDOWN_DURATION'), 10)
const OTP_PATTERN = /^\d{6}$/

export enum CodeState {
  SENT = 'SENT',
  NOT_RECEIVED = 'NOT_RECEIVED',
  RESEND_AVAILABLE = 'RESEND_AVAILABLE',
  RESENT = 'RESENT',
  ERROR = 'ERROR',
}

type UseOtpVerificationOptions<TMasked> = {
  token: string | null
  /** Sends the initial/resend verification code. Returns the masked contact string. */
  sendCode: (token: string) => Promise<TMasked>
  /** Validates the OTP code and updates the contact. */
  updateContact: (token: string, code: string) => Promise<void>
  /** i18n key shown when the initial send fails. */
  sendErrorKey: string
  /** i18n key shown when the submitted OTP is invalid. */
  invalidCodeErrorKey: string
  /** Extracts the masked contact string from the sendCode response. */
  getMasked: (response: TMasked) => string
}

export const useOtpVerification = <TMasked>({
  token,
  sendCode,
  updateContact,
  sendErrorKey,
  invalidCodeErrorKey,
  getMasked,
}: UseOtpVerificationOptions<TMasked>) => {
  const navigate = useNavigate()

  const [maskedContact, setMaskedContact] = useState('')
  const [otpValue, setOtpValue] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [codeState, setCodeState] = useState<CodeState>(CodeState.SENT)
  const [countdown, setCountdown] = useState(COUNTDOWN_DURATION)
  const hasSentInitialCode = useRef(false)
  const sendCodeRef = useRef(sendCode)
  const getMaskedRef = useRef(getMasked)
  useEffect(() => {
    sendCodeRef.current = sendCode
    getMaskedRef.current = getMasked
  })

  useEffect(() => {
    if (!token || hasSentInitialCode.current) return
    hasSentInitialCode.current = true

    const run = async () => {
      try {
        const response = await sendCodeRef.current(token)
        setMaskedContact(getMaskedRef.current(response))
      } catch {
        setError(sendErrorKey)
      }
    }
    run()
  }, [token, sendErrorKey])

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown((c) => Math.max(-1, c - 1))
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (countdown === 0 && (codeState === CodeState.SENT || codeState === CodeState.RESENT)) {
      setCodeState(CodeState.NOT_RECEIVED)
    }
  }, [countdown, codeState])

  const handleResend = useCallback(async () => {
    if (!token) return
    setCountdown(COUNTDOWN_DURATION)
    setCodeState(CodeState.RESENT)
    setError(null)

    try {
      await sendCode(token)
    } catch {
      setCodeState(CodeState.ERROR)
    }
  }, [token, sendCode])

  const handleNotReceivedClick = useCallback(() => {
    setCodeState(CodeState.RESEND_AVAILABLE)
  }, [])

  const handleSubmit = useCallback(
    async (code: string) => {
      if (!token || code.length !== 6) return

      setIsSubmitting(true)
      setError(null)

      try {
        await updateContact(token, code)
        setSuccess(true)
      } catch {
        setError(invalidCodeErrorKey)
        setOtpValue('')
      } finally {
        setIsSubmitting(false)
      }
    },
    [token, updateContact, invalidCodeErrorKey],
  )

  const handleOtpChange = useCallback(
    (value: string) => {
      const trimmed = value.trim()
      setOtpValue(trimmed)
      if (OTP_PATTERN.test(trimmed)) {
        handleSubmit(trimmed)
      }
    },
    [handleSubmit],
  )

  const handleBack = useCallback(() => {
    navigate(-1)
  }, [navigate])

  const handleSave = useCallback(() => {
    handleSubmit(otpValue)
  }, [handleSubmit, otpValue])

  const handleDone = useCallback(() => {
    if (window.history.length <= 1 && typeof window.UIBridge?.onClose === 'function') {
      window.UIBridge.onClose()
    } else {
      navigate(-1)
    }
  }, [navigate])

  return {
    maskedContact,
    otpValue,
    isSubmitting,
    error,
    success,
    codeState,
    countdown,
    handleOtpChange,
    handleResend,
    handleNotReceivedClick,
    handleBack,
    handleSave,
    handleDone,
  }
}
